<script setup lang="ts">
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useForm } from 'vee-validate';
import { yup } from '@clearbanc/validation';

import { JobTitles, OWNER_ROLES } from '@clearbanc/data-common-types';
import { JobTitlesByCorpCountry } from '@/data/owner';
import { RequestStatus } from '@/types/request.types';
import { UserResponseDto } from '@clearbanc/baus-sdk';
import { useToast } from '@/composables/useToast';

import CheckboxGroup from '@/components/forms/groups/CheckboxGroup.vue';
import DropdownGroup from '@/components/forms/groups/DropdownGroup.vue';
import DSButton from 'primevue/button';

// -----
// Setup
// -----

const store = useStore();
const toast = useToast();
const emits = defineEmits(['saveSuccess']);

export type TeamUser = Pick<
  UserResponseDto,
  'id' | 'fullName' | 'isPrimary' | 'jobTitle' | 'jobRoles'
>;

const props = defineProps<{
  user: TeamUser;
}>();

// ---------
// Constants
// ---------

const jobTitles = computed(() =>
  JobTitlesByCorpCountry(store.getters.business.corpCountry),
);

const roleOptions = [
  {
    value: OWNER_ROLES.OWNER,
    label: 'This individual has 25%+ ownership',
  },
];

const validationSchema = yup.object({
  jobTitle: yup.mixed<JobTitles>().nullable(),
  jobRoles: yup
    .array()
    .of(
      yup
        .string<OWNER_ROLES>()
        .oneOf(Object.values(OWNER_ROLES), 'Invalid role selected')
        .required(),
    )
    .nullable(),
});

// ---------
// Variables
// ---------

const updateUserRequestStatus = computed<RequestStatus>(() =>
  store.getters.requestStatus('UPDATE_USER'),
);

const { handleSubmit, isSubmitting } = useForm<{
  jobTitle?: JobTitles;
  jobRoles?: OWNER_ROLES[];
}>({
  validationSchema,
  validateOnMount: false,
  initialValues: {
    jobTitle: props.user.jobTitle as JobTitles | undefined,
    jobRoles: props.user.jobRoles?.includes(OWNER_ROLES.OWNER)
      ? [OWNER_ROLES.OWNER]
      : undefined,
  },
});

// -------
// Methods
// -------

const onSubmit = handleSubmit(async (values) => {
  await validationSchema.validate(values, {
    stripUnknown: true,
  });

  const { jobTitle: updatedJobTitle, jobRoles = [] } = values;

  // Remove any roles already covered by the form
  const cleanRoles = props.user?.jobRoles.filter(
    (role) => !roleOptions.some((option) => option.value === role),
  );

  // Combine existing roles with new ones from the form
  const updatedJobRoles = [...cleanRoles, ...jobRoles];

  const payload = {
    ...(updatedJobRoles && { jobRoles: updatedJobRoles }),
    ...(updatedJobTitle && { jobTitle: updatedJobTitle }),
  };

  await store.dispatchApiAction('UPDATE_USER', {
    id: props.user.id,
    ...payload,
  });

  if (updateUserRequestStatus.value.isSuccess) {
    toast.success(`Changes saved.`);
    emits('saveSuccess');
  } else {
    const errorMessage =
      updateUserRequestStatus.value.errorMessage ||
      'An error occurred while saving changes.';
    toast.error(`Failed to update user: ${errorMessage}`);
  }
});
</script>

<template>
  <div>
    <form
      ref="form"
      class="c-form"
      :style="{ pointerEvents: isSubmitting ? 'none' : 'all' }"
      @submit.prevent="onSubmit"
    >
      <div class="c-group">
        <div class="c-form__row">
          <div class="mb-5">
            <DropdownGroup
              name="jobTitle"
              :label="$t('common.jobTitle')"
              option-label="label"
              option-value="value"
              :options="jobTitles"
              :placeholder="$t('common.selectOption')"
              data-cy="form-jobTitle"
            />
          </div>
          <div>
            <CheckboxGroup
              name="jobRoles"
              :options="roleOptions"
              data-cy="form-jobRoles"
            />
          </div>
        </div>
        <div>
          <DSButton
            label="Save Profile"
            class="w-full mt-4"
            :loading="isSubmitting"
            type="submit"
          />
        </div>
      </div>
    </form>
  </div>
</template>
